import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { exchangeRateService } from "~/services/exchangeRates/exchangeRateService";
import type {
  ExchangeRateFilters,
  ExchangeRateFormData,
  ManualOverrideFormData,
} from "~/types/exchangeRates";

export const EXCHANGE_RATES_QUERY_KEY = ["exchange-rates"];

/**
 * Get all exchange rates with optional filtering
 */
export function useExchangeRates(filters?: ExchangeRateFilters) {
  return useQuery({
    queryKey: [...EXCHANGE_RATES_QUERY_KEY, filters],
    queryFn: () => exchangeRateService.getAll(filters),
  });
}

/**
 * Get exchange rate by ID
 */
export function useExchangeRate(id: string) {
  return useQuery({
    queryKey: [...EXCHANGE_RATES_QUERY_KEY, id],
    queryFn: () => exchangeRateService.getById(id),
    enabled: !!id,
  });
}

/**
 * Get exchange rate for a specific date
 */
export function useExchangeRateByDate(date: string) {
  return useQuery({
    queryKey: [...EXCHANGE_RATES_QUERY_KEY, "date", date],
    queryFn: () => exchangeRateService.getByDate(date),
    enabled: !!date,
  });
}

/**
 * Get today's exchange rate (with fallback)
 */
export function useTodayExchangeRate() {
  return useQuery({
    queryKey: [...EXCHANGE_RATES_QUERY_KEY, "today"],
    queryFn: () => exchangeRateService.getToday(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 30, // Refetch every 30 minutes
  });
}

/**
 * Get recent rates for chart display
 */
export function useRecentExchangeRates(days: number = 30) {
  return useQuery({
    queryKey: [...EXCHANGE_RATES_QUERY_KEY, "recent", days],
    queryFn: () => exchangeRateService.getRecentRates(days),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Create manual override mutation
 */
export function useCreateManualOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ManualOverrideFormData) =>
      exchangeRateService.createManualOverride(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXCHANGE_RATES_QUERY_KEY });
      toast.success("Tipo de cambio actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar tipo de cambio: ${error.message}`);
    },
  });
}

/**
 * Update exchange rate mutation
 */
export function useUpdateExchangeRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ExchangeRateFormData>;
    }) => exchangeRateService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: EXCHANGE_RATES_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...EXCHANGE_RATES_QUERY_KEY, id],
      });
      toast.success("Tipo de cambio actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar tipo de cambio: ${error.message}`);
    },
  });
}

/**
 * Fetch from API mutation (triggers edge function)
 */
export function useFetchExchangeRateFromApi() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (date?: string) => exchangeRateService.fetchFromApi(date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXCHANGE_RATES_QUERY_KEY });
      toast.success("Tipo de cambio obtenido desde API");
    },
    onError: (error: Error) => {
      toast.error(`Error al obtener tipo de cambio: ${error.message}`);
    },
  });
}

/**
 * Currency conversion hooks
 */
export function useUsdToClp(amountUsd: number, date?: string) {
  return useQuery({
    queryKey: ["currency-conversion", "usd-to-clp", amountUsd, date],
    queryFn: () => exchangeRateService.usdToClp(amountUsd, date),
    enabled: amountUsd > 0,
  });
}

export function useClpToUsd(amountClp: number, date?: string) {
  return useQuery({
    queryKey: ["currency-conversion", "clp-to-usd", amountClp, date],
    queryFn: () => exchangeRateService.clpToUsd(amountClp, date),
    enabled: amountClp > 0,
  });
}

/**
 * Rate variation between dates
 */
export function useRateVariation(fromDate: string, toDate: string) {
  return useQuery({
    queryKey: ["rate-variation", fromDate, toDate],
    queryFn: () => exchangeRateService.getRateVariation(fromDate, toDate),
    enabled: !!fromDate && !!toDate,
  });
}
